'use client';

import React, { useState } from 'react';
import {
  Share2,
  Link2,
  Check,
  MessageCircle,
  Twitter,
  Linkedin,
  Facebook,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface ShareControlsProps {
  title: string;
  url: string;
}

export default function ShareControls({ title, url }: ShareControlsProps) {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = [
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      color: '#25D366',
    },
    {
      id: 'twitter',
      label: 'X (Twitter)',
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      color: '#1DA1F2',
    },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: '#0A66C2',
    },
    {
      id: 'facebook',
      label: 'Facebook',
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: '#1877F2',
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="flex items-center gap-1.5 text-xs font-mono-code text-muted-foreground mr-1">
        <Share2 size={12} />
        Share
      </span>

      {/* Copy link */}
      <button
        onClick={copyLink}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold font-display transition-all duration-200 border ${
          copied
            ? 'bg-emerald/15 border-emerald/30 text-emerald' :'glass-card border-border/40 text-muted-foreground hover:text-foreground hover:border-primary/40'
        }`}
        aria-label="Copy article link"
      >
        {copied ? <Check size={12} /> : <Link2 size={12} />}
        {copied ? 'Copied!' : 'Copy link'}
      </button>

      {/* Social links */}
      {shareLinks.map((s) => {
        const Icon = s.icon;
        return (
          <a
            key={s.id}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share on ${s.label}`}
            className="w-8 h-8 glass-card rounded-lg border border-border/40 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all duration-200"
          >
            <Icon size={14} />
          </a>
        );
      })}
    </div>
  );
}
